/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import { Button } from 'reactstrap';
import Dashboard, { DashboardSection } from '@g17eco/molecules/dashboard';
import { materialityAssessmentApi, Tag, useGetSurveyQuery } from '../../api/materiality-assessment';
import { useSurveyGroups } from '../../../../utils/survey';
import { AssessmentProgress } from './AssessmentProgress';
import { QueryWrapper } from '@g17eco/molecules/query/QueryWrapper';
import './styles.scss';
import { ExpandingSurveyQuestionList } from '@features/expanding-survey-question-list/expanding-survey-question-list';
import { UtrvStatus } from '@constants/status';
import { NoData } from '@g17eco/molecules/no-data';
import { Option } from '@g17eco/molecules/select/SelectFactory';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { FilterToggle } from '@g17eco/molecules/filter-toggle';
import { Filters, MinimalSurveyData } from './types';
import { AssessmentDropdown } from './AssessmentDropdown';
import { AssessmentToolbar } from './AssessmentToolbar';
import { BlockingLoader } from '@g17eco/atoms/loader';
import { CompleteButton } from '@features/survey/button/CompleteButton';
import { useHistory } from 'react-router-dom';
import { generateUrl } from '../../../../routes/util';
import { ROUTES } from '../../../../constants/routes';
import { OnChangeValue } from 'react-select';
import { useToggle } from '@hooks/useToggle';
import { AssessmentFilters } from './AssessmentFilters';
import { useMemo, useState } from 'react';
import { useGetSurveyUsersQuery } from '@api/survey-users';
import { ScopeQuestionGroup } from '@g17eco/types/survey';
import { SurveyOverviewMode } from '@g17eco/types/surveyOverview';
import G17Client from '@services/G17Client';
import { DownloadType } from '@g17eco/types/download';
import { SearchDebounce } from '@features/survey/survey-question-list-toolbar/partials/SearchQuestions';
import { generateSurveyQuestionsFlexSearchMap } from '../../../../selectors/blueprint';
import { Document } from 'flexsearch';
import classnames from 'classnames';
import { useAppDispatch } from '@reducers/index';
import { disableByDelegationStatus, disableBySearch, disableByStatus } from '@utils/survey/getDisableUtrs';
import { UnlockAssessmentAlert } from '../../components/assessment/UnlockAssessmentAlert';
import { isAssessmentPaid } from '../../utils';
import { useUnlockAssessment } from '../../hooks/useUnlockAssessment';
import LockedAssessmentImage from '../../../../images/assessment-lock/locked-assessment.png';
import { LockedSection } from '../locked-section/LockedSection';
import { MATERIALITY_TRACKER_MESSAGES } from '../../constants';

interface AssessmentProps {
  initiativeId: string;
  surveys: MinimalSurveyData[];
  surveyId: string;
}

const useFilters = ({
  surveyGroups,
  searchIndex,
}: {
  surveyGroups: ScopeQuestionGroup[];
  searchIndex: Document<unknown>;
}) => {
  const [showFilters, toggleShowFilters] = useToggle(false);
  const [filters, setFilters] = useState<Filters>({});

  const handleChangeFilters = (key: keyof Filters, value?: string | string[]) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const generateDisabledUtrs = () => [
    ...disableBySearch({ surveyGroups, searchText: filters.searchText, searchIndex }),
    ...(filters.status ? disableByStatus({ surveyGroups, filterByStatus: [filters.status] }) : []),
    ...disableByDelegationStatus({ surveyGroups, filterByDelegationStatus: filters.delegation }),
  ];
  const disabledUTRs = generateDisabledUtrs();

  return {
    showFilters,
    toggleShowFilters,
    filters,
    handleChangeFilters,
    disabledUTRs,
  };
};

const features = [
  'Personalised results tailored to your business',
  'Downloadable materiality reports & recommended metrics',
  'Actionable insights from 400+ topic combinations',
];

export const Assessment = (props: AssessmentProps) => {
  const { initiativeId, surveys, surveyId } = props;

  const surveyQuery = useGetSurveyQuery({ surveyId });
  const { data: survey } = surveyQuery;
  const surveyGroups = useSurveyGroups(SurveyOverviewMode.Universal, survey);
  const { data: surveyUsers } = useGetSurveyUsersQuery({ surveyId });
  const { handleUnlock } = useUnlockAssessment();

  const searchIndex = useMemo(() => generateSurveyQuestionsFlexSearchMap(survey), [survey]);
  const { showFilters, toggleShowFilters, filters, handleChangeFilters, disabledUTRs } = useFilters({
    surveyGroups,
    searchIndex,
  });

  const history = useHistory();
  const goToInsights = () =>
    history.push(generateUrl(ROUTES.MATERIALITY_TRACKER_INSIGHTS, { initiativeId, insightSurveyId: surveyId }));
  const dispatch = useAppDispatch();
  const handleReloadQuestion = async () => {
    dispatch(materialityAssessmentApi.util.invalidateTags([Tag.SurveyList]));
    await surveyQuery.refetch();
  };

  const handleReloadOnCompleteAssessment = async () => {
    handleReloadQuestion();
    goToInsights();
  };

  const hasUnverifiedQuestions = survey
    ? survey.fragmentUniversalTrackerValues.some((utrv) => utrv?.status !== UtrvStatus.Verified)
    : true;

  const isComplete = Boolean(survey?.completedDate);
  const isOutputsLocked = Boolean(survey?.outputsLockedDate);
  const onClickItem = (op: OnChangeValue<Option<string>, false>) => {
    if (survey && op?.value === survey._id) {
      return;
    }
    history.push(generateUrl(ROUTES.MATERIALITY_TRACKER_SURVEY, { initiativeId, surveyId: op?.value }));
  };

  const downloadLatestAssessmentCSV = async () => {
    return G17Client.downloadLatestAssessmentAnswers({
      surveyId,
      initiativeId,
      type: DownloadType.Csv,
    });
  };

  return (
    <div>
      {surveyQuery.data ? <UnlockAssessmentAlert survey={surveyQuery.data} /> : null}
      <Dashboard className='assessmentContainer'>
        <QueryWrapper
          query={surveyQuery}
          onFetching={() => <></>}
          onLoading={BlockingLoader}
          onError={() => <NoData text='There was an error fetching your assessments. Please try again.' />}
          onNoData={() => <NoData text='No assessments currently available.' />}
          onSuccess={(survey) => (
            <>
              <div className='d-flex align-items-center px-3 mb-3'>
                <AssessmentProgress utrvs={survey.fragmentUniversalTrackerValues} />
                {isComplete ? (
                  <SimpleTooltip text={isOutputsLocked ? MATERIALITY_TRACKER_MESSAGES.OUTPUTS_LOCKED : ''}>
                    <Button color='primary' className='btn-lg ml-3' onClick={goToInsights} disabled={isOutputsLocked}>
                      <i className='fal fa-square-poll-vertical mr-2' />
                      View results
                    </Button>
                  </SimpleTooltip>
                ) : (
                  <CompleteButton
                    disabled={hasUnverifiedQuestions}
                    isUserAdmin={true}
                    survey={survey}
                    color='secondary'
                    handleReload={handleReloadOnCompleteAssessment}
                    notCompletedText='Submit assessment'
                    notCompletedIcon=''
                    showPopup={false}
                    classes={{ btn: 'btn-lg ml-3' }}
                    tooltip={hasUnverifiedQuestions ? 'Please verify all questions before submitting' : ''}
                  />
                )}
              </div>
              <DashboardSection>
                <div className='d-flex align-items-center mb-3'>
                  <AssessmentDropdown selectedSurvey={survey} surveys={surveys} onClickItem={onClickItem} />
                  <div className='d-flex align-items-center ml-auto gap-1'>
                    <Button onClick={downloadLatestAssessmentCSV} disabled={!isAssessmentPaid(survey)}>
                      <i className='fal fa-file-csv text-ThemeIconSecondary mr-2' />
                      Download assessment
                    </Button>
                  </div>
                </div>
                <div className='d-flex gap-3'>
                  <SearchDebounce
                    handleChange={(value: string) => handleChangeFilters('searchText', value)}
                    defaultValue={filters.searchText}
                  />
                  <FilterToggle
                    toggleFilters={toggleShowFilters}
                    showExtendedFilters={showFilters}
                    label='Filters'
                    reverse
                  />
                </div>
                {showFilters ? (
                  <AssessmentFilters filters={filters} onChangeFilters={handleChangeFilters} users={surveyUsers} />
                ) : null}
                <div className={classnames('mt-4', { 'completed-survey-group-wrapper': !!survey?.completedDate })}>
                  <ExpandingSurveyQuestionList
                    survey={survey}
                    surveyId={surveyId}
                    initiativeId={initiativeId}
                    surveyGroups={surveyGroups}
                    handleReload={handleReloadQuestion}
                    Toolbar={AssessmentToolbar}
                    disabledUTRs={disabledUTRs}
                  />
                  {!isAssessmentPaid(survey) && (
                    <LockedSection
                      title='Unlock your full assessment'
                      features={features}
                      buttonText='Unlock full assessment'
                      onUnlock={() => handleUnlock(survey)}
                      backgroundImage={LockedAssessmentImage}
                    />
                  )}
                </div>
              </DashboardSection>
            </>
          )}
        />
      </Dashboard>
    </div>
  );
};
